import { usePhone } from "../../theme/PhoneContext"
import { useTheme } from "../../theme/useTheme"
import { PHONE_CODE_LENGTH, formatPhoneRest, maskPhoneChange } from "../../utils/phone"
import { Box, IconButton, alpha } from "@mui/material"
import React, { useRef } from "react"

// portfolio nav-toggle glyphs (feather-style, 18px, stroke 1.5) — icon shows
// the current mode
const SunIcon = (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
    >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
)

const MoonIcon = (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
)

// mirrors the portfolio toggle: both glyphs stacked in one grid cell,
// crossfade + rotate swap on theme change
export const ColorSchemeToggle: React.FC<{ size?: number }> = ({ size = 48 }) => {
    const { themeSettings } = useTheme()
    const isDark = themeSettings.isDarkMode

    const iconSpan = (hidden: boolean) => ({
        gridArea: "1 / 1",
        display: "grid",
        placeItems: "center",
        transition: "opacity 0.3s ease, transform 0.3s ease",
        opacity: hidden ? 0 : 1,
        transform: hidden ? "rotate(90deg) scale(0.6)" : "none",
    })

    return (
        <IconButton
            aria-label="Toggle color theme"
            title="Toggle theme"
            onClick={() => themeSettings.setThemeMode(isDark ? "light" : "dark")}
            disableRipple
            sx={{
                width: size,
                height: size,
                // square hover fill, matching the hamburger half of the chip
                borderRadius: 0,
                display: "inline-grid",
                placeItems: "center",
                color: "text.secondary",
                transition: "color 0.12s ease",
                "&:hover": {
                    color: "primary.main",
                    // kill MUI's own action.hover fill explicitly
                    bgcolor: "transparent",
                    "& svg": {
                        fill: (t) => t.palette.primary.main,
                        stroke: (t) => t.palette.primary.main,
                    },
                },
            }}
        >
            <Box component="span" sx={iconSpan(isDark)}>
                {SunIcon}
            </Box>
            <Box component="span" sx={iconSpan(!isDark)}>
                {MoonIcon}
            </Box>
        </IconButton>
    )
}

// segmented pill — text-only underlines read as links, so the pill shape
// carries the toggle affordance; shared width aligns the sidebar column
const MonoTextToggleGroup = <T extends string>({
    value,
    options,
    onChange,
    width,
}: {
    value: T
    options: { value: T; label: string }[]
    onChange: (value: T) => void
    width?: number
}) => (
    <Box
        sx={{
            display: "inline-flex",
            width,
            // never squash vertically when the sidebar runs out of height —
            // it scrolls instead
            flexShrink: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            fontFamily: '"DM Mono", monospace',
            fontSize: 13,
        }}
    >
        {options.map((o, i) => {
            const active = o.value === value
            return (
                <Box
                    key={o.value}
                    component="button"
                    type="button"
                    onClick={() => onChange(o.value)}
                    sx={{
                        flex: width ? 1 : undefined,
                        background: active ? (t) => alpha(t.palette.primary.main, 0.12) : "none",
                        color: active ? "primary.main" : "text.secondary",
                        border: "none",
                        borderLeft: i > 0 ? "1px solid" : "none",
                        borderLeftColor: "divider",
                        // monospace advances round up on fractional zoom;
                        // nowrap keeps the longest label inside its flexed half
                        px: 1,
                        py: "5px",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        transition: "background-color 0.15s ease, color 0.15s ease",
                        "&:hover": active ? undefined : { color: (t) => t.palette.primary.main },
                    }}
                >
                    {o.label}
                </Box>
            )
        })}
    </Box>
)

export const AudienceToggle: React.FC = () => {
    const { audience } = useTheme()

    return (
        <MonoTextToggleGroup
            width={250}
            value={audience.mode}
            onChange={audience.setAudienceMode}
            options={[
                { value: "thai", label: "thai" },
                { value: "international", label: "international" },
            ]}
        />
    )
}

export const SkillsToggle: React.FC = () => {
    const { skills } = useTheme()

    return (
        <MonoTextToggleGroup
            width={250}
            value={skills.style}
            onChange={skills.setSkillsStyle}
            options={[
                { value: "simple", label: "simple" },
                { value: "highlighted", label: "highlighted" },
            ]}
        />
    )
}

// typed digits get normal ink, remaining zeros a dim backdrop — never
// selectable, never clickable. DM Mono's fixed advance keeps the layers
// aligned via ch-unit offsets
const REST_LENGTH = 9

const padCode = (digits: string) => digits + "0".repeat(PHONE_CODE_LENGTH - digits.length)
const padRest = (digits: string) => formatPhoneRest(digits + "0".repeat(REST_LENGTH - digits.length))

// exact-fit flex row, not MUI's adorned input — that flexes wider than its
// content, leaving dead space before ")"
const PhoneField = ({
    inputRef,
    prefix,
    suffix,
    inputCh,
    backdrop,
    ...input
}: {
    inputRef: React.RefObject<HTMLInputElement | null>
    prefix?: string
    suffix?: string
    inputCh: number
    backdrop: React.ReactNode
} & React.InputHTMLAttributes<HTMLInputElement>) => (
    <Box
        sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            height: 40,
            px: "14px",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            fontFamily: '"DM Mono", monospace',
            fontSize: "1rem",
            color: "text.primary",
            "&:focus-within": { borderColor: "primary.main" },
            "& input": {
                border: 0,
                outline: "none",
                background: "transparent",
                padding: 0,
                font: "inherit",
                color: "inherit",
                height: "100%",
                // nudge typed digits vertically — tune by eye
                transform: "translateY(-1px)",
                // exact digit width — no flex slack to center against
                width: `${inputCh}ch`,
                "&::placeholder": { color: (t) => alpha(t.palette.text.primary, 0.2), opacity: 1 },
            },
        }}
    >
        {prefix && (
            <Box component="span" sx={{ whiteSpace: "pre" }}>
                {prefix}
            </Box>
        )}
        <input ref={inputRef} type="tel" {...input} />
        {suffix && (
            <Box component="span" sx={{ whiteSpace: "pre" }}>
                {suffix}
            </Box>
        )}
        {backdrop}
    </Box>
)

export const PhoneToggle: React.FC = () => {
    const phone = usePhone()
    const codeRef = useRef<HTMLInputElement>(null)
    const restRef = useRef<HTMLInputElement>(null)

    // dim zero backdrop: offset = field padding (14px) + prefix + typed
    // length, in ch
    const zeroTail = (typed: string, full: string, prefixCh: number) => {
        const tail = full.slice(typed.length)
        if (!tail) return null
        return (
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    left: `calc(14px + ${prefixCh + typed.length}ch)`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontFamily: '"DM Mono", monospace',
                    fontSize: "1rem",
                    color: (t) => alpha(t.palette.text.primary, 0.2),
                    pointerEvents: "none",
                    userSelect: "none",
                    whiteSpace: "pre",
                }}
            >
                {tail}
            </Box>
        )
    }

    return (
        <Box sx={{ width: 250, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            {/* same toggle family as the sidebar's other controls */}
            <MonoTextToggleGroup
                width={250}
                value={phone.show ? "show" : "hide"}
                onChange={(v) => phone.setShow(v === "show")}
                options={[
                    { value: "hide", label: "hide phone" },
                    { value: "show", label: "show phone" },
                ]}
            />
            {phone.show && (
                // recenters on the sidebar axis — the toggles above are 250
                // wide, this pair is narrower
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <PhoneField
                        inputRef={codeRef}
                        prefix="+ ("
                        suffix=")"
                        inputCh={PHONE_CODE_LENGTH}
                        autoFocus
                        placeholder="00"
                        value={phone.code}
                        backdrop={zeroTail(phone.code, padCode(phone.code), 3)}
                        onChange={(e) => {
                            const next = maskPhoneChange(e, phone.code, PHONE_CODE_LENGTH)
                            phone.setCode(next)
                            // code full → jump to the rest of the number
                            if (
                                next.length === PHONE_CODE_LENGTH &&
                                phone.code.length < PHONE_CODE_LENGTH
                            ) {
                                restRef.current?.focus()
                            }
                        }}
                    />
                    {/* digits-only state: the mask renders the dashes and
                        backspace deletes through them — empty field + backspace
                        steps back into the code box */}
                    <PhoneField
                        inputRef={restRef}
                        inputCh={11}
                        placeholder="00-000-0000"
                        value={formatPhoneRest(phone.rest)}
                        backdrop={zeroTail(formatPhoneRest(phone.rest), padRest(phone.rest), 0)}
                        onChange={(e) => phone.setRest(maskPhoneChange(e, phone.rest, REST_LENGTH))}
                        onKeyDown={(e) => {
                            if (e.key === "Backspace" && phone.rest === "") {
                                e.preventDefault()
                                codeRef.current?.focus()
                                const el = codeRef.current
                                if (el) el.setSelectionRange(el.value.length, el.value.length)
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}
